import React, { useState, useRef } from 'react';
import { Card } from './ui/card';
import { HelpCircle, Download, ChevronLeft, ChevronRight } from 'lucide-react';

export default function DeviceComparisonCard({ devices = {}, dailyDevices = [], dateRange = 7 }) {
  const [hoveredDay, setHoveredDay] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const scrollRef = useRef(null);

  // Overall counts and percentages
  const desktopCount = devices?.Desktop || 0;
  const tabletCount  = devices?.Tablet || 0;
  const mobileCount  = devices?.Mobile || 0;
  const totalCount   = desktopCount + tabletCount + mobileCount || 1;

  const desktopPct = Math.round((desktopCount / totalCount) * 100);
  const tabletPct  = Math.round((tabletCount / totalCount) * 100);
  const mobilePct  = Math.round((mobileCount / totalCount) * 100);

  const formatDayLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const dayName = d.toLocaleDateString('en', { weekday: 'short' });
      const monthDay = `${d.getMonth() + 1}/${d.getDate()}`;
      return { dayName, monthDay };
    } catch {
      return { dayName: 'Day', monthDay: dateStr };
    }
  };

  // Map of dailyDevices for easy lookup
  const dailyMap = {};
  (dailyDevices || []).forEach((d) => {
    if (d?.date) dailyMap[d.date] = d;
  });

  // Build complete date list for the entire timeframe (dateRange days)
  const targetDaysCount = Math.max(dateRange || 7, 1);
  const today = new Date();
  const columns = [];

  for (let i = targetDaysCount - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const { dayName, monthDay } = formatDayLabel(dateStr);
    
    const existing = dailyMap[dateStr];
    const desktop = existing?.Desktop || 0;
    const tablet  = existing?.Tablet || 0;
    const mobile  = existing?.Mobile || 0;
    const total   = desktop + tablet + mobile;

    columns.push({
      date: dateStr,
      dayName,
      monthDay,
      desktop,
      tablet,
      mobile,
      total: total || (targetDaysCount > 1 ? 0 : totalCount),
    });
  }

  // Find max daily total to scale column heights gracefully
  const maxDailyTotal = Math.max(...columns.map((c) => c.desktop + c.tablet + c.mobile), 1);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8,Date,Desktop,Tablet,Mobile\n"
      + columns.map(c => `${c.date},${c.desktop},${c.tablet},${c.mobile}`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `devices_breakdown_${dateRange}d.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const displayedDay = hoveredDay || activeDay;

  return (
    <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none p-6 border border-slate-100 dark:border-border transition-colors flex flex-col justify-between h-full min-h-[280px] sm:min-h-[450px]">
      {/* ── Header ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Desktop vs. tablet vs. mobile</h3>
            <div className="relative group">
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
              <div className="absolute left-6 top-0 z-30 hidden group-hover:block bg-[#021426] text-white text-xs px-3 py-2 rounded-xl shadow-xl w-60 border border-[#FEAB3D]/20">
                Daily breakdown of traffic by device type (Desktop, Tablet, Mobile) over the selected timeframe ({dateRange} days).
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleExport}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Download CSV"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>

        {/* ── Legend ── */}
        <div className="flex flex-wrap items-center gap-6 mb-4 text-sm">
          {/* Desktop */}
          <div className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded bg-[#FEAB3D] inline-block shadow-sm" />
            <span className="font-bold text-slate-900 dark:text-white">{displayedDay ? Math.round(((displayedDay.desktop || 0) / (displayedDay.total || 1)) * 100) : desktopPct} %</span>
            <span className="text-slate-500 dark:text-slate-400 font-medium">Desktop</span>
          </div>

          {/* Tablet */}
          <div className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded bg-[#F59E0B] inline-block shadow-sm" />
            <span className="font-bold text-slate-900 dark:text-white">{displayedDay ? Math.round(((displayedDay.tablet || 0) / (displayedDay.total || 1)) * 100) : tabletPct} %</span>
            <span className="text-slate-500 dark:text-slate-400 font-medium">Tablet</span>
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded bg-[#10B981] inline-block shadow-sm" />
            <span className="font-bold text-slate-900 dark:text-white">{displayedDay ? Math.round(((displayedDay.mobile || 0) / (displayedDay.total || 1)) * 100) : mobilePct} %</span>
            <span className="text-slate-500 dark:text-slate-400 font-medium">Mobile</span>
          </div>
        </div>
      </div>

      {/* ── Stacked Bar Chart & Scroll Area ── */}
      <div className="relative pt-4 pb-1">
        {/* Tooltip on Hover */}
        {hoveredDay && (
          <div
            className="absolute z-30 pointer-events-none bg-[#021426] text-white text-xs px-3.5 py-2.5 rounded-xl shadow-2xl border border-[#FEAB3D]/30 transition-all duration-150"
            style={{
              left: `${hoveredDay.leftPercent}%`,
              top: '-15px',
              transform: 'translate(-50%, -100%)',
            }}
          >
            <p className="font-semibold text-[#FEAB3D] mb-1">{hoveredDay.date}</p>
            <div className="space-y-0.5 font-mono">
              <p className="text-orange-300">Desktop: {hoveredDay.desktop} ({Math.round((hoveredDay.desktop / (hoveredDay.total || 1)) * 100)}%)</p>
              <p className="text-[#F59E0B]">Tablet: {hoveredDay.tablet} ({Math.round((hoveredDay.tablet / (hoveredDay.total || 1)) * 100)}%)</p>
              <p className="text-emerald-300">Mobile: {hoveredDay.mobile} ({Math.round((hoveredDay.mobile / (hoveredDay.total || 1)) * 100)}%)</p>
            </div>
          </div>
        )}

        {/* Scrollable Columns Wrapper */}
        <div
          ref={scrollRef}
          className={`flex items-end h-56 pb-2 space-x-1.5 custom-scrollbar transition-all ${
            columns.length > 7 ? 'overflow-x-auto scroll-smooth' : 'justify-between'
          }`}
        >
          {columns.map((col, idx) => {
            const daySum = col.desktop + col.tablet + col.mobile;
            const barHeightPct = maxDailyTotal > 0 && daySum > 0 ? Math.max((daySum / maxDailyTotal) * 100, 12) : 5;

            const desktopShare = daySum > 0 ? (col.desktop / daySum) * 100 : 100;
            const tabletShare  = daySum > 0 ? (col.tablet / daySum) * 100 : 0;
            const mobileShare  = daySum > 0 ? (col.mobile / daySum) * 100 : 0;

            const isSelected = activeDay?.date === col.date || hoveredDay?.date === col.date;
            const leftPct = Math.min(Math.max(((idx + 0.5) / columns.length) * 100, 10), 90);

            return (
              <div
                key={col.date || idx}
                className={`flex flex-col items-center h-full justify-end group cursor-pointer flex-shrink-0 ${
                  columns.length > 7 ? 'w-[44px]' : 'flex-1 max-w-[75px]'
                }`}
                onClick={() => setActiveDay(activeDay?.date === col.date ? null : col)}
                onMouseEnter={() => setHoveredDay({ ...col, leftPercent: leftPct })}
                onMouseLeave={() => setHoveredDay(null)}
              >
                {/* Stacked Pillar */}
                <div
                  className={`w-full flex flex-col justify-end overflow-hidden rounded-t-sm transition-all duration-200 ${
                    isSelected ? 'opacity-100 ring-2 ring-[#FEAB3D] scale-[1.02]' : 'opacity-90 hover:opacity-100'
                  }`}
                  style={{ height: `${barHeightPct}%` }}
                >
                  {/* Top: Mobile (Green) */}
                  {mobileShare > 0 && (
                    <div
                      className="bg-[#10B981] w-full transition-all duration-300"
                      style={{ height: `${mobileShare}%` }}
                    />
                  )}
                  {/* Middle: Tablet (Yellow) */}
                  {tabletShare > 0 && (
                    <div
                      className="bg-[#F59E0B] w-full transition-all duration-300 border-t border-white/20"
                      style={{ height: `${tabletShare}%` }}
                    />
                  )}
                  {/* Bottom: Desktop (Orange) */}
                  {desktopShare > 0 && (
                    <div
                      className="bg-[#FEAB3D] w-full transition-all duration-300 border-t border-white/20"
                      style={{ height: `${desktopShare}%` }}
                    />
                  )}
                </div>

                {/* Date Label Box */}
                <div
                  className={`w-full text-center py-1.5 mt-1 rounded-md border transition-colors ${
                    isSelected
                      ? 'bg-[#FEAB3D] text-[#021426] border-[#FEAB3D] font-bold shadow'
                      : 'bg-slate-50 dark:bg-slate-800/60 border-slate-100 dark:border-slate-800'
                  }`}
                >
                  <div className={`text-[10px] font-semibold ${isSelected ? 'text-[#021426]' : 'text-slate-500 dark:text-slate-400'}`}>{col.dayName}</div>
                  <div className={`text-[10px] font-mono ${isSelected ? 'text-[#021426]' : 'text-slate-400 dark:text-slate-500'}`}>{col.monthDay}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll Controls bar for long timeframes */}
        {columns.length > 7 && (
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-800/60 text-xs text-slate-400">
            <button
              type="button"
              onClick={() => handleScroll('left')}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 flex items-center gap-1 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Scroll Left
            </button>
            <span className="text-[11px] font-mono text-slate-400">
              {columns[0]?.monthDay} — {columns[columns.length - 1]?.monthDay} ({columns.length} days)
            </span>
            <button
              type="button"
              onClick={() => handleScroll('right')}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 flex items-center gap-1 transition-colors"
            >
              Scroll Right <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
